import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Skeleton,
  Stack,
  Text
} from "@chakra-ui/react";
import { RiArrowRightLine } from "react-icons/ri";
import { TextGray } from "components/Atomos/Texts/TextGray";
import { PlanoDiretorNoPonto } from "store/modules/consultaprevia/actions/obterPlanoDiretorNoPonto";
import { AreaEspecialNoPonto } from "store/modules/consultaprevia/types";
import { CartaoCamada } from "./CartaoCamada";
import { RestricoesAbsolutas } from "./RestricoesAbsolutas";
import { RodapeVersao } from "./RodapeVersao";

type Props = {
  isRequesting: boolean;
  resultado: PlanoDiretorNoPonto | null;
  errors?: string[];
  /** Quando informado, cada zona ganha um atalho para a ficha completa. */
  onVerFicha?: (sigla: string) => void;
};

const badgesDaArea = (area: AreaEspecialNoPonto) => (
  <>
    {area.categoria && (
      <Badge colorScheme="purple" fontSize="0.65rem">
        {area.categoria}
      </Badge>
    )}
    {area.proibeEdificacao && (
      <Badge colorScheme="red" fontSize="0.65rem">
        Proíbe edificação
      </Badge>
    )}
    {area.proibeParcelamentoSolo && (
      <Badge colorScheme="red" fontSize="0.65rem">
        Proíbe parcelamento do solo
      </Badge>
    )}
  </>
);

/**
 * O que a versão publicada diz sobre uma coordenada. Compartilhado entre o
 * mapa da consulta prévia e o formulário do licenciamento — os dois perguntam
 * a mesma coisa ao mesmo endpoint, então devem responder igual.
 */
export const ResultadoPonto = ({
  isRequesting,
  resultado,
  errors = [],
  onVerFicha
}: Props) => {
  if (isRequesting) {
    return (
      <Stack spacing={4} mt="4">
        <Skeleton height="20px" width="60%" />
        {[1, 2, 3].map((i) => (
          <Box key={i}>
            <Skeleton height="40px" borderRadius="md" />
            <Skeleton mt={2} height="80px" borderRadius="md" />
          </Box>
        ))}
      </Stack>
    );
  }

  if (errors.length > 0) {
    return (
      <Alert status="error" borderRadius="md" mt="4">
        <AlertIcon />
        <Box>
          {errors.map((erro) => (
            <Text key={erro} fontSize="sm">
              {erro}
            </Text>
          ))}
        </Box>
      </Alert>
    );
  }

  // Consultou e voltou vazio: o endpoint do ponto responde 204 quando não há
  // versão do plano diretor publicada. Os três lugares que montam este
  // componente só o fazem depois de disparar a consulta, então "sem resultado"
  // aqui não é "ainda não perguntou" — é a resposta. Devolver null deixava a
  // lateral (e o modal do licenciamento) abertos e mudos, como se a consulta
  // tivesse falhado em silêncio.
  if (!resultado) {
    return (
      <Alert status="info" borderRadius="md" mt="4">
        <AlertIcon />
        <Text fontSize="sm">
          Não há versão do plano diretor publicada. Ainda não é possível
          consultar o zoneamento deste ponto.
        </Text>
      </Alert>
    );
  }

  const { zona, areasEspeciais = [], restricoesAbsolutas = [] } = resultado;

  return (
    <Box>
      <TextGray fontSize="xs">
        {`Ponto consultado — latitude ${resultado.latitude?.toFixed(
          6
        )}, longitude ${resultado.longitude?.toFixed(6)}`}
      </TextGray>

      <Box mt="4">
        <RestricoesAbsolutas restricoes={restricoesAbsolutas} />

        {/* Fora de zona não é erro: é a resposta. O motivo vem do backend e
            distingue "não há versão publicada" de "o ponto está fora do
            perímetro urbano". */}
        {resultado.foraDeZona && (
          <Alert status="warning" borderRadius="md" mb="4">
            <AlertIcon />
            <Text fontSize="sm">
              {resultado.motivoForaDeZona ??
                "O ponto não está dentro de nenhuma zona urbana da versão publicada."}
            </Text>
          </Alert>
        )}

        {zona && (
          <>
            <Text fontSize="sm" fontWeight="bold" mb="2">
              Zona urbana
            </Text>
            <CartaoCamada
              sigla={zona.sigla}
              nome={zona.nome}
              tipo={zona.tipo}
              descricao={zona.descricao}
              estilo={zona.estilo}
              parametros={zona.parametros}
              usosSolo={zona.usosSolo}
              acao={
                onVerFicha && (
                  <Button
                    size="xs"
                    variant="mainOutline"
                    rightIcon={<RiArrowRightLine />}
                    onClick={() => onVerFicha(zona.sigla)}
                  >
                    Ver ficha completa
                  </Button>
                )
              }
            />
          </>
        )}

        <Text fontSize="sm" fontWeight="bold" mt="4" mb="2">
          {`Áreas especiais incidentes (${areasEspeciais.length})`}
        </Text>

        {areasEspeciais.length === 0 ? (
          <TextGray fontSize="sm">
            Nenhuma área especial incide sobre este ponto.
          </TextGray>
        ) : (
          areasEspeciais.map((area) => (
            <CartaoCamada
              key={area.id}
              sigla={area.sigla}
              nome={area.nome}
              tipo={area.tipo}
              descricao={area.descricao}
              estilo={area.estilo}
              parametros={area.parametros}
              usosSolo={area.usosSolo}
              badges={badgesDaArea(area)}
            />
          ))
        )}
      </Box>

      <RodapeVersao versao={resultado.versao} />
    </Box>
  );
};
